import type { UserDto } from '@lagerfeuer/shared';
import { CustomStatusText } from '../profile/CustomStatusText';
import { useQuery } from '@tanstack/react-query';
import { Menu as MenuIcon, MessageCircle, Plus, Search, UserRound, Users } from 'lucide-react';
import { useId, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { Avatar, STATUS_LABELS } from '../../components/Avatar';
import { anchorFrom } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { ApiError, errorMessage } from '../../lib/api';
import { usersApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName, dmName } from '../../store/selectors';
import { toast, useUi } from '../../store/ui';
import { ServerIcon } from '../shell/ServerIcon';
import { isOnline, matchesUser, openDm, useContacts, useDebounced, type Contact } from './dmUtils';
import './dms.css';

type Tab = 'online' | 'all';

/** Startseite `/channels/@me`: Kontakte (online/alle), Nutzersuche, letzte Unterhaltungen. */
export function DmHome() {
  const navigate = useNavigate();
  const contacts = useContacts();
  const [tab, setTab] = useState<Tab>('online');
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const debounced = useDebounced(query.trim(), 300);
  const tabsId = useId();
  const search = useQuery({
    queryKey: ['user-search', debounced],
    queryFn: () => usersApi.search(debounced, 25),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });
  const known = new Set(contacts.map((c) => c.user.id));
  const shown = contacts.filter((c) => (tab === 'all' || isOnline(c)) && matchesUser(c.user, query));
  const remote = debounced.length >= 2 ? (search.data ?? []).filter((u) => !known.has(u.id) && !u.isShadow) : [];
  const onlineCount = contacts.filter(isOnline).length;

  const message = async (user: UserDto) => {
    setOpening(user.id);
    try {
      const ch = await openDm([user.id]);
      void navigate(paths.dm(ch.id));
    } catch (e) {
      toast.error(
        'Direktnachricht nicht möglich',
        e instanceof ApiError && e.code === 'DM_BLOCKED' ? `${displayName(user)} nimmt keine Direktnachrichten von dir an.` : errorMessage(e),
      );
      setOpening(null);
    }
  };

  return (
    <div className="dm-home" data-testid="dm-home">
      <header className="channel-header dm-home-header">
        <button className="icon-btn mobile-only" onClick={() => useUi.getState().setNavOpen(true)} aria-label="Navigation öffnen" data-testid="dm-home-open-nav">
          <MenuIcon size={20} />
        </button>
        <Users size={22} aria-hidden="true" />
        <h1 className="channel-title">Kontakte</h1>
        <span className="header-sep" aria-hidden="true" />
        <div className="dm-home-tabs" role="tablist" aria-label="Kontakte filtern">
          {(['online', 'all'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              id={`${tabsId}-${t}`}
              aria-selected={tab === t}
              aria-controls={`${tabsId}-panel`}
              className="dm-home-tab"
              onClick={() => setTab(t)}
              data-testid={`dm-home-tab-${t}`}
            >
              {t === 'online' ? `Online (${onlineCount})` : `Alle (${contacts.length})`}
            </button>
          ))}
        </div>
        <span className="spacer" />
        <button className="btn btn-primary btn-sm" onClick={() => overlays.open({ type: 'newDm' })} data-testid="dm-home-new">
          <Plus size={14} aria-hidden="true" /> <span className="dm-home-new-label">Neue Nachricht</span>
        </button>
      </header>
      <div className="dm-home-body">
        <main className="dm-home-main scroll" id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${tab}`}>
          <div className="dm-home-search">
            <Search size={16} aria-hidden="true" />
            <input
              className="input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Kontakte filtern oder Nutzer suchen"
              aria-label="Kontakte filtern oder Nutzer suchen"
              data-testid="dm-home-search"
            />
          </div>

          <h2 className="dm-home-section">{tab === 'online' ? 'Online' : 'Alle Kontakte'} – {shown.length}</h2>
          <ul className="dm-home-list" role="list" data-testid="dm-home-contacts">
            {shown.map((c) => (
              <ContactRow key={c.user.id} contact={c} busy={opening === c.user.id} onMessage={() => void message(c.user)} />
            ))}
          </ul>
          {shown.length === 0 && remote.length === 0 && (
            <div className="empty" data-testid="dm-home-empty">
              <UserRound size={40} aria-hidden="true" />
              <p>
                {query.trim()
                  ? debounced.length >= 2 && search.isFetching
                    ? 'Suche …'
                    : 'Niemand gefunden.'
                  : tab === 'online'
                    ? 'Gerade ist niemand online. Am Lagerfeuer ist trotzdem Platz.'
                    : 'Noch keine Kontakte. Suche oben nach einem Benutzernamen.'}
              </p>
            </div>
          )}

          {remote.length > 0 && (
            <>
              <h2 className="dm-home-section">Weitere Nutzer – {remote.length}</h2>
              <ul className="dm-home-list" role="list" data-testid="dm-home-results">
                {remote.map((u) => (
                  <ContactRow
                    key={u.id}
                    contact={{ user: u, dmChannelId: null, status: 'OFFLINE', customStatus: null }}
                    busy={opening === u.id}
                    onMessage={() => void message(u)}
                    testId="dm-home-result"
                  />
                ))}
              </ul>
            </>
          )}
          {search.isError && <p className="error-text small">Suche fehlgeschlagen: {errorMessage(search.error)}</p>}
        </main>
        <RecentDms />
      </div>
    </div>
  );
}

function ContactRow({ contact, busy, onMessage, testId = 'dm-home-contact' }: { contact: Contact; busy: boolean; onMessage: () => void; testId?: string }) {
  const { user } = contact;
  const name = displayName(user);
  return (
    <li className="dm-home-row" data-testid={testId} data-user-id={user.id}>
      <button className="dm-home-person" onClick={(e) => overlays.profile(user.id, anchorFrom(e.currentTarget))} aria-label={`Profil von ${name}`} data-testid="dm-home-profile">
        <Avatar user={user} size={36} showStatus />
        <span className="dm-home-person-text">
          <span className="dm-home-name truncate">
            {name}
            {user.isBot && <span className="bot-tag">BOT</span>}
          </span>
          <span className="small faint truncate">
            <CustomStatusText status={contact.customStatus} fallback={contact.status === 'OFFLINE' ? user.username : STATUS_LABELS[contact.status]} />
          </span>
        </span>
      </button>
      <Tooltip label="Nachricht senden">
        <button className="icon-btn dm-home-action" disabled={busy} onClick={onMessage} aria-label={`Nachricht an ${name}`} data-testid="dm-home-message">
          <MessageCircle size={18} />
        </button>
      </Tooltip>
    </li>
  );
}

/** Letzte Unterhaltungen (DMs/Gruppen), rechte Spalte am Desktop. */
function RecentDms() {
  const ids = useAppStore((s) => s.dmIds);
  return (
    <aside className="dm-home-recent scroll" aria-label="Letzte Unterhaltungen" data-testid="dm-home-recent">
      <h2 className="dm-home-section">Letzte Unterhaltungen</h2>
      {ids.length === 0 && <p className="small faint">Noch keine Unterhaltungen.</p>}
      <ul role="list" className="dm-home-recent-list">
        {ids.slice(0, 8).map((id) => (
          <RecentItem key={id} channelId={id} />
        ))}
      </ul>
    </aside>
  );
}

function RecentItem({ channelId }: { channelId: string }) {
  const ch = useAppStore((s) => s.channels[channelId]);
  const meId = useAppStore((s) => s.me?.id);
  if (!ch) return null;
  const name = dmName(ch, meId);
  const other = (ch.recipients ?? []).find((u) => u.id !== meId);
  return (
    <li>
      <NavLink to={paths.dm(channelId)} className="dm-home-recent-item" data-testid="dm-home-recent-item" data-channel-id={channelId}>
        {ch.type === 'GROUP_DM' ? <ServerIcon server={{ name, iconUrl: ch.iconUrl }} size={28} /> : <Avatar user={other} size={28} showStatus />}
        <span className="truncate">{name}</span>
      </NavLink>
    </li>
  );
}
